import React, {useState} from 'react';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {useSearchParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkTrainingSchedule} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {TrainingLandingTabs} from 'src/pages/Modules/Training/shared/TrainingLandingTabs';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {TrainingScheduleViewMode} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {ScheduleCalendarView} from './components/ScheduleCalendarView';
import {ScheduleListView} from './components/ScheduleListView';
import type {ScheduleFilters} from './components/ScheduleFiltersBar';
import {
  EMPTY_SCHEDULE_FILTERS,
  ScheduleFiltersBar,
} from './components/ScheduleFiltersBar';

/**
 * Schedule — all classrooms across every course, top-level. Calendar view
 * for at-a-glance planning, list view for filtering/sorting; both share one
 * filter bar and click through to the Class Workspace via an explicit
 * action (no row-click navigation). View mode lives in the URL (`?view=`)
 * so the Class Workspace back button returns to the view it was opened from.
 */
const SchedulePage: React.FC = () => {
  useSetDocumentTitle(TrainingDocumentTitle);

  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: TrainingScheduleViewMode =
    searchParams.get('view') === 'list' ? 'list' : 'calendar';
  const [filters, setFilters] = useState<ScheduleFilters>(
    EMPTY_SCHEDULE_FILTERS
  );

  const handleViewModeChange = (value: string) => {
    setSearchParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.set('view', value);
        return next;
      },
      {replace: true}
    );
  };

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkTrainingSchedule.icon}>
          Schedule
        </PageHeader.Title>
        <PageHeader.Actions>
          <SegmentedControl
            value={viewMode}
            onChange={handleViewModeChange}
            data={[
              {label: 'Calendar', value: 'calendar'},
              {label: 'List', value: 'list'},
            ]}
          />
        </PageHeader.Actions>
      </PageHeader>

      <TrainingLandingTabs active="schedule" />

      <ScheduleFiltersBar filters={filters} onChange={setFilters} />

      {viewMode === 'calendar' ? (
        <ScheduleCalendarView filters={filters} />
      ) : (
        <ScheduleListView filters={filters} />
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingSchedule(),
  element: <SchedulePage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default SchedulePage;
